import React, { useState } from 'react';
import { Modal, Button, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { auth } from '../../Firebase';
import { updatePassword } from 'firebase/auth';
import Close from "../../images/close-icon.svg";
import EyeClose from "../../images/eye-close.svg";
import { toast } from 'react-toastify';

const ChangePasswordModal = ({ showChangePass, onClose }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isStrongPassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()[\]{}\-_=+\\|;:'",.<>\/?]).{8,}$/;
    return regex.test(password);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const toggleVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;
    const newErrors = {};

    if (!oldPassword.trim()) {
      newErrors.oldPassword = "Please enter your current password";
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "Please enter a new password";
    } else if (oldPassword === newPassword) {
      newErrors.newPassword = "New password cannot be the same as the old password";
    } else if (!isStrongPassword(newPassword)) {
      newErrors.newPassword = "Must be 8+ characters with uppercase, lowercase, number, and special character";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        setErrors({ general: "No user is logged in" });
        setLoading(false);
        return;
      }
      await updatePassword(user, newPassword);
      toast('Password changed successfully!', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});
      onClose();
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/wrong-password') {
        setErrors({ oldPassword: "Current password is incorrect" });
      } else if (err.code === 'auth/weak-password') {
        setErrors({ newPassword: "Password is too weak" });
      } else if (err.code === 'auth/requires-recent-login') {
        setErrors({ general: "Please log in again and try changing the password" });
      } else {
        setErrors({ general: err.message || "Something went wrong" });
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <Modal
      show={showChangePass}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      contentClassName="password-modal-content"
      dialogClassName="password-modal-dialog"
    >
      <Link
        onClick={onClose}
        className="modal-close-floating"
        aria-label="Close"
      >
        <Image src={Close} alt="Close" />
      </Link>

      <Modal.Body className="password-modal-body p-0">
        <div className="head-bg">
          <h5 className="password-modal-title">Change Password</h5>
        </div>
        <div className="inner-modal-content inner-radius">
          <p>
            Enter your current password and set a new one of your choice. Make sure it's strong and secure.
          </p>

          <form className='mt-4' onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
            <div className="form-field">
              <label htmlFor="oldPassword">Enter Old Password</label>
              <div className="password-input">
                <input
                  type={showPassword.old ? "text" : "password"}
                  id="oldPassword"
                  name="oldPassword"
                  placeholder="********"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  required
                />
                <button type="button" className="toggle-password" onClick={() => toggleVisibility('old')} aria-label="Toggle password visibility">
                  <Image src={EyeClose} alt="Toggle" />
                </button>
              </div>
              {errors.oldPassword && <div className="text-danger small">{errors.oldPassword}</div>}
            </div>


            <div className="form-field">
              <label htmlFor="newPassword">Create New Password</label>
              <div className="password-input">
                <input
                  type={showPassword.new ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  placeholder="********"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                />
                <button type="button" className="toggle-password" onClick={() => toggleVisibility('new')} aria-label="Toggle password visibility">
                  <Image src={EyeClose} alt="Toggle" />
                </button>
              </div>
              {errors.newPassword && <div className="text-danger small">{errors.newPassword}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input">
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="********"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <button type="button" className="toggle-password" onClick={() => toggleVisibility('confirm')} aria-label="Toggle password visibility">
                  <Image src={EyeClose} alt="Toggle" />
                </button>
              </div>
              {errors.confirmPassword && <div className="text-danger small">{errors.confirmPassword}</div>}
            </div>
          </form>
        </div>
      </Modal.Body>

      <div className="d-flex gap-2 justify-content-end btn-footer px-3 pb-3">
        <Button variant="secondary" className="modal-btn cancel-btn" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button className="login-button m-0" onClick={handleSubmit} disabled={loading}>
          {loading ? "Updating..." : "Continue"}
        </Button>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
